import { type Control, Controller } from "react-hook-form";
import { INFRACTION_ATTACHMENT_TYPE } from "../constants/infraction-create.constant";
import type {
	InfractionAttachmentSlotKey,
	InfractionAttachmentSlots,
} from "../handlers/map-infraction-attachments-to-slots";
import type { InfractionCreateSchema } from "../schemas/infraction-create.schema";
import { InfractionCreateAttachmentField } from "./infraction-create-attachment-field";

type InfractionCreateAttachmentsSectionProps = {
	control: Control<InfractionCreateSchema>;
	isPending: boolean;
	requireAttachments?: boolean;
	existingAttachments?: InfractionAttachmentSlots | null;
	allowRemoveExisting?: boolean;
	onRemoveExistingAttachment?: (slot: InfractionAttachmentSlotKey) => void;
	onAttachmentChange?: () => void;
	errors?: Partial<
		Record<
			"infractionNoticeFile" | "penaltyNotificationFile" | "receiptsFile",
			string | undefined
		>
	>;
};

export function InfractionCreateAttachmentsSection({
	control,
	isPending,
	requireAttachments = false,
	existingAttachments,
	allowRemoveExisting = false,
	onRemoveExistingAttachment,
	onAttachmentChange,
	errors,
}: InfractionCreateAttachmentsSectionProps) {
	const persistedAttachments = existingAttachments ?? {
		infractionNotice: null,
		penaltyNotification: null,
		receipts: null,
	};

	return (
		<section className="flex flex-col gap-6">
			<h3 className="font-bold text-fg-primary text-md leading-6">
				Anexos
				{requireAttachments ? (
					<span className="ml-0.5 text-error-primary">*</span>
				) : null}
			</h3>

			<Controller
				control={control}
				name="infractionNoticeFile"
				render={({ field }) => (
					<InfractionCreateAttachmentField
						allowRemoveExisting={allowRemoveExisting}
						disabled={isPending}
						error={errors?.infractionNoticeFile}
						existingFile={persistedAttachments.infractionNotice}
						file={field.value ?? null}
						htmlFor="infraction-attachment-notice"
						isRequired={requireAttachments}
						label="Auto de Infração"
						onExistingFileRemove={() => {
							onRemoveExistingAttachment?.("infractionNotice");
						}}
						onFileChange={(file) => {
							onAttachmentChange?.();
							field.onChange(file);
						}}
					/>
				)}
			/>

			<Controller
				control={control}
				name="penaltyNotificationFile"
				render={({ field }) => (
					<InfractionCreateAttachmentField
						allowRemoveExisting={allowRemoveExisting}
						disabled={isPending}
						error={errors?.penaltyNotificationFile}
						existingFile={persistedAttachments.penaltyNotification}
						file={field.value ?? null}
						htmlFor="infraction-attachment-penalty"
						label="Notificação de Penalidade"
						onExistingFileRemove={() => {
							onRemoveExistingAttachment?.("penaltyNotification");
						}}
						onFileChange={(file) => {
							onAttachmentChange?.();
							field.onChange(file);
						}}
					/>
				)}
			/>

			<Controller
				control={control}
				name="receiptsFile"
				render={({ field }) => (
					<InfractionCreateAttachmentField
						allowRemoveExisting={allowRemoveExisting}
						disabled={isPending}
						error={errors?.receiptsFile}
						existingFile={persistedAttachments.receipts}
						file={field.value ?? null}
						htmlFor="infraction-attachment-receipts"
						label="Comprovantes"
						onExistingFileRemove={() => {
							onRemoveExistingAttachment?.("receipts");
						}}
						onFileChange={(file) => {
							onAttachmentChange?.();
							field.onChange(file);
						}}
					/>
				)}
			/>
		</section>
	);
}

export function buildInfractionAttachmentFiles(
	values: Pick<
		InfractionCreateSchema,
		"infractionNoticeFile" | "penaltyNotificationFile" | "receiptsFile"
	>
): { type: number; file: File }[] {
	const attachmentFiles: { type: number; file: File }[] = [];

	if (values.infractionNoticeFile) {
		attachmentFiles.push({
			type: INFRACTION_ATTACHMENT_TYPE.INFRACTION_NOTICE,
			file: values.infractionNoticeFile,
		});
	}
	if (values.penaltyNotificationFile) {
		attachmentFiles.push({
			type: INFRACTION_ATTACHMENT_TYPE.PENALTY_NOTIFICATION,
			file: values.penaltyNotificationFile,
		});
	}
	if (values.receiptsFile) {
		attachmentFiles.push({
			type: INFRACTION_ATTACHMENT_TYPE.RECEIPTS,
			file: values.receiptsFile,
		});
	}

	return attachmentFiles;
}

export function hasInfractionAttachmentFiles(
	values: Pick<
		InfractionCreateSchema,
		"infractionNoticeFile" | "penaltyNotificationFile" | "receiptsFile"
	>
): boolean {
	return buildInfractionAttachmentFiles(values).length > 0;
}
